import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';

import type { Scene, TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { nonEditableAssetError } from './non-editable-asset';
import { resolveSceneHandle } from './scene-addressing';
import { getSceneTranscriptTokens } from './transcript-partition';
import { VideoStateTool } from './VideoStateTool';

import { describeFacePlacement, findCutsBetweenWords } from 'js/src/agent/video/clipFraming';
import {
    getClipFraming,
    getSpeakerCrop,
    rememberSpeakerCrop,
} from 'js/src/agent/video/clipFramingStore';
import { setFullMediaTranslation } from 'js/src/agent/video-state';
import { isNonRecommendable } from 'js/src/agent/video-state/serialization/assetSerializer';
import {
    canReframeMedia,
    getFullMediaTranslation,
    getMediaCropDimensions,
    getMediaFramingPreset,
} from 'js/src/agent/video-state/serialization/mediaHelpers';
import type { BoundingBox } from 'js/src/client';
import { setBrightnessUserOverride, setCropUserOverride } from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import { MEDIA_ROTATION, MEDIA_ZOOM } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import {
    calculateMinZoomFromRotation,
    createContainCropState,
    createFillCropState,
    cropToBoundingBox,
} from 'js/src/libs/crop-utils';
import { saveRawLuminary } from 'js/src/luminary';
import { isTalkingHeadSceneGroup } from 'js/src/luminary/luminary-selectors';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    applyUserMediaKenburnOperation,
    applyUserMediaTrimOperation,
    getFaceDetectionsOperation,
} from 'js/src/luminary/luminary-operations';

const PLAYBACK_MODES = ['loop', 'hold'] as const;
type PlaybackModeArg = (typeof PLAYBACK_MODES)[number];

// The editor's two framing presets: cover the frame, or show the whole media inside it.
const FRAMING_PRESETS = ['fill', 'fit'] as const;
type FramingPresetArg = (typeof FRAMING_PRESETS)[number];

// Mirrors the manual advanced-media-controls speed slider (0.1x–3x).
const MIN_PLAYBACK_RATE = 0.1;
const MAX_PLAYBACK_RATE = 3;

/** The part of the recording a slot shows, as fractions of the recording's picture. */
function cropWindow(
    asset: TimeAwareMediaAsset,
    recording: { width: number; height: number },
): { left: number; right: number; top: number; bottom: number } {
    const { width: slotWidth, height: slotHeight } = asset.mediaTargetResolution;
    const sourceRatio = recording.width / recording.height;
    const isHeightBound = slotWidth / slotHeight < sourceRatio;
    const scaledWidth = (isHeightBound ? slotHeight * sourceRatio : slotWidth) * asset.scaleX;
    const scaledHeight = (isHeightBound ? slotHeight : slotWidth / sourceRatio) * asset.scaleY;
    return {
        left: 0.5 + (asset.xTranslation * slotWidth - slotWidth / 2) / scaledWidth,
        right: 0.5 + (asset.xTranslation * slotWidth + slotWidth / 2) / scaledWidth,
        top: 0.5 + (asset.yTranslation * slotHeight - slotHeight / 2) / scaledHeight,
        bottom: 0.5 + (asset.yTranslation * slotHeight + slotHeight / 2) / scaledHeight,
    };
}

const CROP_EDGE_TOLERANCE = 0.002;

/**
 * Whether the crop holds the framed face whole and the recording covers the whole slot, worked
 * out from the crop itself rather than judged from a render, so it can be trusted over a
 * description of the picture.
 */
function describeCropCheck(
    asset: TimeAwareMediaAsset,
    recording: { width: number; height: number },
    framedFace: BoundingBox,
    faceNumber: number,
): string {
    const window = cropWindow(asset, recording);
    const edges = (checks: [boolean, string][]) =>
        checks.filter(([failed]) => failed).map(([, edge]) => edge);
    const cutEdges = edges([
        [framedFace.x < window.left - CROP_EDGE_TOLERANCE, 'left'],
        [framedFace.x + framedFace.width > window.right + CROP_EDGE_TOLERANCE, 'right'],
        [framedFace.y < window.top - CROP_EDGE_TOLERANCE, 'top'],
        [framedFace.y + framedFace.height > window.bottom + CROP_EDGE_TOLERANCE, 'bottom'],
    ]);
    const barEdges = edges([
        [window.left < -CROP_EDGE_TOLERANCE, 'left'],
        [window.right > 1 + CROP_EDGE_TOLERANCE, 'right'],
        [window.top < -CROP_EDGE_TOLERANCE, 'top'],
        [window.bottom > 1 + CROP_EDGE_TOLERANCE, 'bottom'],
    ]);
    if (cutEdges.length === 0 && barEdges.length === 0) {
        return `crop check: face ${faceNumber} is whole inside the slot, and the recording covers the whole slot, so it shows no bars or empty space`;
    }
    const problems = [
        cutEdges.length > 0 &&
            `face ${faceNumber} is cut off at the slot's ${cutEdges.join(' and ')} edge`,
        barEdges.length > 0 &&
            `the slot shows past the recording's ${barEdges.join(' and ')} edge, an empty bar: raise scale`,
    ].filter(Boolean);
    return `crop check: ${problems.join('; ')}`;
}

/**
 * The other faces that fall inside the part of the recording a slot shows, e.g. "face 2" or
 * "face 1 (partly) and face 2". The inverse of `cropToBoundingBox`: the slot shows its own size
 * of the scaled source, centred where the translation puts it.
 */
function describeOtherFacesInCrop(
    asset: TimeAwareMediaAsset,
    recording: { width: number; height: number },
    faces: BoundingBox[],
    framedFace: BoundingBox,
): string | null {
    const window = cropWindow(asset, recording);
    const shown = faces.flatMap((face, index) => {
        if (face === framedFace) {
            return [];
        }
        const overlaps =
            face.x < window.right &&
            face.x + face.width > window.left &&
            face.y < window.bottom &&
            face.y + face.height > window.top;
        if (!overlaps) {
            return [];
        }
        const isWhole =
            face.x >= window.left &&
            face.x + face.width <= window.right &&
            face.y >= window.top &&
            face.y + face.height <= window.bottom;
        return [`face ${index + 1}${isWhole ? '' : ' (partly)'}`];
    });
    return shown.length > 0 ? shown.join(' and ') : null;
}

/** The one camera angle a scene's words play over, from the clip's shot table, ignoring b-roll. */
function findSceneCameraAngle(
    state: RootState,
    scene: Scene,
    recordingUuid: string,
): string | null {
    const sceneGroup = scene.sceneGroup;
    const framing = sceneGroup && getClipFraming(sceneGroup.id, recordingUuid);
    if (!framing) {
        return null;
    }
    const angles = new Set(
        (getSceneTranscriptTokens(state, scene) ?? [])
            .filter((token) => !token.isPause)
            .flatMap((token) =>
                framing.shots.filter(
                    (shot) =>
                        !shot.isBroll &&
                        token.startTime >= shot.startMs &&
                        token.startTime < shot.endMs,
                ),
            )
            .map((shot) => shot.angle),
    );
    return angles.size === 1 ? [...angles][0] : null;
}

/**
 * A warning when the scene plays across a change of camera angle from the clip's shot table:
 * frame_speaker framed the angle at the scene's middle, so the crop doesn't fit the other one.
 * Jump cuts within one angle don't count, since the same crop fits both sides.
 */
function describeSpannedAngleChanges(
    state: RootState,
    scene: Scene,
    recordingUuid: string,
): string | null {
    const sceneGroup = scene.sceneGroup;
    const framing = sceneGroup && getClipFraming(sceneGroup.id, recordingUuid);
    if (!framing) {
        return null;
    }
    const wordStartTimesMs = (getSceneTranscriptTokens(state, scene) ?? [])
        .filter((token) => !token.isPause)
        .map((token) => token.startTime);
    const changes = findCutsBetweenWords(framing.angleChangeTimesMs, wordStartTimesMs);
    if (changes.length === 0) {
        return null;
    }
    const shotsAfterChanges = framing.shots.filter((shot) => changes.includes(shot.startMs));
    const where = shotsAfterChanges
        .map((shot) =>
            shot.isBroll
                ? `into b-roll at shot ${shot.number}`
                : `into angle ${shot.angle} at shot ${shot.number}`,
        )
        .join(', ');
    return `this scene plays across ${changes.length === 1 ? 'a change of camera angle' : `${changes.length} changes of camera angle`} (${where}), and the crop was framed on the footage at its middle, so it doesn't fit the rest: cover each angle with its own scene, using the words the shot table's speaker scenes list (b-roll needs no scene), and frame each one`;
}

export const editMediaSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The asset name (e.g. media_1)'),
    scale: z
        .number()
        .min(10)
        .max(1000)
        .optional()
        .describe('Scale value for the media in percentage (zoom). Above 100 zooms in.'),
    position: z
        .object({
            x: z.number().meta({ description: 'X value in pixels.', default: 0 }),
            y: z.number().meta({ description: 'Y value in pixels.', default: 0 }),
        })
        .optional()
        .describe(
            "Translate (pan) the media relative to the center of the asset's bounds. The translation range is determined by the asset's mediaXTranslationRange and mediaYTranslationRange property. If the range is too narrow, increase the media's scale.",
        ),
    framing: z
        .enum(FRAMING_PRESETS)
        .optional()
        .describe(
            '"fill" resets the media to cover its frame (scale 100%, centered, edges cropped). "fit" zooms out until the whole media is visible inside the frame, centered, with empty space around it — for logos, slides, screenshots and anything that must not be cropped. Cannot be combined with scale or position in the same call.',
        ),
    rotation: z
        .number()
        .min(MEDIA_ROTATION.MIN_VALUE)
        .max(MEDIA_ROTATION.MAX_VALUE)
        .optional()
        .describe(
            `Rotation in degrees (${MEDIA_ROTATION.MIN_VALUE} to ${MEDIA_ROTATION.MAX_VALUE}); 0 is upright, positive turns clockwise. Use a few degrees to straighten a tilted photo. Rotating media that covers its frame raises the scale just enough to keep the corners covered.`,
        ),
    flip_horizontal: z
        .boolean()
        .optional()
        .describe(
            'Mirror the media left-to-right (true) or not (false), e.g. so a person faces the text. An absolute state, not a toggle.',
        ),
    flip_vertical: z
        .boolean()
        .optional()
        .describe(
            'Mirror the media top-to-bottom (true) or not (false). An absolute state, not a toggle.',
        ),
    frame_speaker: z
        .number()
        .int()
        .min(1)
        .optional()
        .describe(
            "In a talking-head scene group, show one person from the recording in this media slot, cropped in close on their face (found by face detection at this scene's midpoint): 1 is the leftmost face in the recording's picture, 2 the next one to the right, and so on. Sets the position for you, so it can't be combined with position or framing. Pass scale with it to frame the face at that zoom instead of the default close-up (a scale change on its own afterwards does not keep the face centred). Works on any media slot: a slot that isn't already the talking head is linked to the recording, replacing its media, so it plays in sync with the speech. For a split screen of two speakers, use a design with two media slots and frame one speaker in each.",
        ),
    kenburn: z
        .enum(VideoData.ALL_BACKGROUND_ANIMATIONS)
        .optional()
        .describe(
            'Ken Burns motion for a still image (video clips always play with none). Vary it across consecutive image scenes rather than defaulting to zoom_in: alternate zoom_in/zoom_out, and pan in the direction the subject faces or moves.',
        ),
    brightness: z
        .number()
        .min(0)
        .max(1.5)
        .optional()
        .describe(
            'Brightness level for the media asset (0=completely black, 0.5=dimmed, 1=default, 1.2=bright, 1.5=very bright)',
        ),
    volume: z
        .number()
        .min(0)
        .max(100)
        .optional()
        .describe('Volume level for video assets, from 0 (silent) to 100 (full)'),
    start_time: z
        .number()
        .min(0)
        .optional()
        .describe(
            'Trim in-point: milliseconds into the source clip where playback should start. Video assets only.',
        ),
    end_time: z
        .number()
        .min(0)
        .optional()
        .describe(
            'Trim out-point: milliseconds into the source clip where playback should end. Video assets only.',
        ),
    playback_mode: z
        .enum(PLAYBACK_MODES)
        .optional()
        .describe(
            'How a video that is shorter than its scene behaves: "loop" repeats the clip, "hold" freezes on the last frame. Video assets only.',
        ),
    playback_rate: z
        .number()
        .min(MIN_PLAYBACK_RATE)
        .max(MAX_PLAYBACK_RATE)
        .optional()
        .describe(
            'Playback speed multiplier (1 = normal, 0.5 = half speed, 2 = double speed). Video assets only.',
        ),
});
export type EditMediaArgs = z.infer<typeof editMediaSchema>;

/**
 * Edits an existing media asset in place: how it is framed (scale, position, Ken Burns, brightness),
 * how it plays (trim, loop/hold, speed, volume). It never changes *which* clip is in the slot — use
 * `set_media` for that. Splitting the two keeps each tool's parameters focused: `set_media` picks the
 * clip, `edit_media` adjusts it.
 */
export class EditMediaTool extends VideoStateTool<typeof editMediaSchema> {
    protected override readonly schema = editMediaSchema;
    protected override readonly toolName = TOOL_NAMES.editMedia;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Adjust an existing media asset without changing which clip is in the slot: reframe it (a fit/fill framing preset, scale, position, rotation, flips, Ken Burns, brightness) or change how it plays (trim with start_time/end_time, loop-or-hold a short clip, speed, volume).

Scale and position reframe the media inside a fixed frame, and the position axes are inverted — load the \`${SKILL_NAMES.adjustMedia}\` skill before using them.`;

    public override getToolDisplayName(): string {
        return 'Editing media';
    }

    public get statusIcon(): string {
        return 'tune';
    }

    private lastEditedSceneIndex: number | undefined;

    protected override onEditApplied(): void {
        // Reframing the first scene changes the thumbnail.
        if (this.lastEditedSceneIndex === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    protected override preprocessArgs(args: EditMediaArgs): EditMediaArgs {
        // position.x/y carry a 0 default that .meta emits but does not fill at parse time, so a
        // partial position like { x: 5 } would hard-fail on the missing y — fill it here.
        if (args.position && typeof args.position === 'object' && !Array.isArray(args.position)) {
            const position = args.position as { x?: number; y?: number };
            args = { ...args, position: { x: 0, y: 0, ...position } };
        }
        return args;
    }

    public async execute(args: EditMediaArgs, context: ToolExecutionContext) {
        const {
            asset_name,
            scale,
            position,
            framing,
            rotation,
            flip_horizontal,
            flip_vertical,
            frame_speaker,
            kenburn,
            brightness,
            volume,
            start_time,
            end_time,
            playback_mode,
            playback_rate,
        } = args;
        const { parentActionGroup } = context;

        const changes: string[] = [];
        const notes: string[] = [];
        // Requested values the asset already has — reported as "No change" if nothing else moved.
        const unchanged: string[] = [];
        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            this.lastEditedSceneIndex = scene_index;

            const scene = this.luminary.scenes[scene_index];
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId} which uses design ${scene.sceneDesign}`;
            }
            if (!asset.isEditable || isNonRecommendable(scene, asset_name, this.getState())) {
                return nonEditableAssetError(asset_name, sceneShortId);
            }
            if (!VideoData.isMediaAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a media asset`;
            }

            // A preset and an explicit zoom/pan would overwrite each other, so refuse the mix
            // before anything is mutated rather than silently letting one win.
            if (framing !== undefined && (scale !== undefined || position !== undefined)) {
                return `Error: framing cannot be combined with scale or position in the same call — apply the ${framing} preset first, then adjust scale/position in a second call.`;
            }
            if (frame_speaker !== undefined && (framing !== undefined || position !== undefined)) {
                return `Error: frame_speaker positions the crop on the face itself, so it cannot be combined with framing or position in the same call. Pass scale with it to frame the face at that zoom.`;
            }
            const recording = scene.sceneGroup?.mediaAsset?.sourceData;
            if (
                frame_speaker !== undefined &&
                (!scene.sceneGroup || !isTalkingHeadSceneGroup(scene.sceneGroup) || !recording)
            ) {
                return `Error: frame_speaker only works in a talking-head scene group, where it frames a person from the group's recording. Scene ${sceneShortId} isn't one; crop ${asset_name} with scale and position.`;
            }
            const sceneAngle =
                frame_speaker !== undefined && recording
                    ? findSceneCameraAngle(this.getState(), scene, recording.uuid)
                    : null;
            // The same face on the same angle gets the same crop every time the angle returns, so
            // the picture doesn't jump; detecting again would land a slightly different crop.
            const reusedCrop =
                frame_speaker !== undefined && scale === undefined && sceneAngle && scene.sceneGroup
                    ? getSpeakerCrop(
                          scene.sceneGroup.id,
                          sceneAngle,
                          frame_speaker,
                          asset.mediaTargetResolution,
                      )
                    : null;
            const speakerCrop =
                frame_speaker === undefined
                    ? null
                    : reusedCrop
                      ? { face: reusedCrop.face, faces: reusedCrop.faces }
                      : await this.resolveSpeakerFace(scene.id, sceneShortId, frame_speaker);
            if (speakerCrop && 'error' in speakerCrop) {
                return `Error: ${speakerCrop.error}`;
            }

            let framingZoom: number | null = null;
            if (framing !== undefined) {
                const resolved = this.resolveFraming(asset, asset_name, framing);
                if ('error' in resolved) {
                    return `Error: ${resolved.error}`;
                }
                if (resolved.alreadyApplied) {
                    unchanged.push(`framing is already ${framing}`);
                } else {
                    framingZoom = resolved.zoom;
                }
            }

            const wantsPlaybackEdit =
                start_time !== undefined ||
                end_time !== undefined ||
                playback_mode !== undefined ||
                playback_rate !== undefined;

            // The presenter slot of a talking-head scene mirrors the scene group's recording. Its
            // playback is owned by the transcript, so trimming/looping/re-speeding it here would fight
            // the narration, and its audio is the group's voice, which every scene in the group
            // shares — point the agent at the scene-group tools for both. Reframing still applies.
            if (asset.linkToSceneGroup && wantsPlaybackEdit) {
                return `Error: ${asset_name} in scene ${sceneShortId} is the talking head — its playback follows the speech and cannot be trimmed, looped or re-timed here. Use ${TOOL_NAMES.trimSceneGroup} to shorten the recording instead. Reframing (scale/position) still works through ${TOOL_NAMES.editMedia}; the recording's volume is set with ${TOOL_NAMES.setSceneGroupVolume}.`;
            }
            if (asset.linkToSceneGroup && volume !== undefined) {
                return `Error: ${asset_name} in scene ${sceneShortId} is the talking head — its audio is the scene group's voice, shared by every scene in the group. Set it with ${TOOL_NAMES.setSceneGroupVolume} (voice_volume) instead. Reframing (scale/position) still works through ${TOOL_NAMES.editMedia}.`;
            }

            // Validate the trim BEFORE mutating anything, so an invalid trim can't return an error
            // after reframe edits have already been written (they would be left applied and, since the
            // final checkpoint never runs, uncheckpointed). Trim is video-only; an image trim is
            // skipped with a note further down, so it needs no validation here.
            const wantsTrim = start_time !== undefined || end_time !== undefined;
            let resolvedTrim: { start: number; end: number } | null = null;
            if (wantsTrim && asset.isVideo) {
                const trim = this.resolveTrim(asset, start_time, end_time);
                if ('error' in trim) {
                    return `Error: ${trim.error}`;
                }
                resolvedTrim = trim;
            }

            const { applyUserMediaKenburn, applyUserMediaTrim } = makeOperationsCallable(
                { applyUserMediaKenburnOperation, applyUserMediaTrimOperation },
                this.luminary,
                this.dispatch,
                this._getState,
            );

            if (kenburn !== undefined) {
                applyUserMediaKenburn(scene.id, asset.id, kenburn, false, parentActionGroup);
                changes.push(`media animation to ${kenburn}`);
            }

            if (brightness !== undefined) {
                asset.brightness = brightness;
                this.dispatch(
                    setBrightnessUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
                changes.push(`media brightness to ${brightness}`);
            }

            if (scale !== undefined && frame_speaker === undefined) {
                const scaleValue = scale / 100;
                asset.scaleX = scaleValue;
                asset.scaleY = scaleValue;
                changes.push('media scale');
            }
            if (position !== undefined) {
                setFullMediaTranslation(asset, position.x, position.y);
                const xDirection = position.x === 0 ? 'center' : position.x < 0 ? 'right' : 'left';
                const yDirection = position.y === 0 ? 'center' : position.y < 0 ? 'down' : 'up';
                changes.push(
                    `media position to ${position.x} (${xDirection}), ${position.y} (${yDirection})`,
                );
            }
            if (speakerCrop && frame_speaker !== undefined && recording) {
                // Linking makes the slot play the group's recording on the group's clock, so every
                // framed slot shows the same moment: how the editor builds split and stack.
                if (!asset.linkToSceneGroup) {
                    asset.linkToSceneGroup = true;
                    changes.push(`linked ${asset_name} to the recording`);
                }
                if (reusedCrop) {
                    asset.scaleX = reusedCrop.scale;
                    asset.scaleY = reusedCrop.scale;
                    asset.xTranslation = reusedCrop.xTranslation;
                    asset.yTranslation = reusedCrop.yTranslation;
                } else {
                    cropToBoundingBox(speakerCrop.face, asset, recording);
                    if (scale !== undefined) {
                        // The pan that centres the face grows in step with the zoom.
                        const zoomChange = scale / 100 / asset.scaleX;
                        asset.scaleX = scale / 100;
                        asset.scaleY = scale / 100;
                        asset.xTranslation *= zoomChange;
                        asset.yTranslation *= zoomChange;
                    }
                    if (sceneAngle && scene.sceneGroup) {
                        rememberSpeakerCrop(
                            scene.sceneGroup.id,
                            sceneAngle,
                            frame_speaker,
                            asset.mediaTargetResolution,
                            {
                                sceneShortId,
                                scale: asset.scaleX,
                                xTranslation: asset.xTranslation,
                                yTranslation: asset.yTranslation,
                                face: speakerCrop.face,
                                faces: speakerCrop.faces,
                            },
                        );
                    }
                }
                changes.push(
                    `framing to face ${frame_speaker} of ${speakerCrop.faces.length}, at the ${describeFacePlacement(speakerCrop.face)} of the picture (scale ${Math.round(asset.scaleX * 100)}%, position ${getFullMediaTranslation(asset)})${reusedCrop ? `, the same crop as scene ${reusedCrop.sceneShortId} on camera angle ${sceneAngle}, so the picture doesn't jump when the angle returns (pass scale to frame it afresh)` : ''}`,
                );
                const otherFaces = speakerCrop.faces
                    .map((found, index) => ({ found, number: index + 1 }))
                    .filter(({ found }) => found !== speakerCrop.face)
                    .map(
                        ({ found, number }, index) =>
                            `face ${number}${index === 0 ? ' is' : ''} at the ${describeFacePlacement(found)}`,
                    );
                if (otherFaces.length > 0) {
                    notes.push(otherFaces.join(', '));
                }
                const alsoShown = describeOtherFacesInCrop(
                    asset,
                    recording,
                    speakerCrop.faces,
                    speakerCrop.face,
                );
                if (alsoShown) {
                    notes.push(
                        `this crop also shows ${alsoShown}, so the slot holds more than one person's picture; leave scale out for the close-up, or raise it`,
                    );
                }
                notes.push(describeCropCheck(asset, recording, speakerCrop.face, frame_speaker));
                const spannedAngles = describeSpannedAngleChanges(
                    this.getState(),
                    scene,
                    recording.uuid,
                );
                if (spannedAngles) {
                    notes.push(spannedAngles);
                }
            }
            if (framingZoom !== null) {
                asset.scaleX = framingZoom;
                asset.scaleY = framingZoom;
                asset.xTranslation = 0;
                asset.yTranslation = 0;
                changes.push(
                    `framing to ${framing} (scale ${Math.round(framingZoom * 100)}%, centered)`,
                );
            }
            let transformChanged = false;
            if (rotation !== undefined) {
                if (Math.abs(asset.transformation.angle - rotation) < 0.5) {
                    unchanged.push(`rotation is already ${rotation}°`);
                } else {
                    asset.transformation.angle = rotation;
                    transformChanged = true;
                    changes.push(
                        `rotation to ${rotation}°${this.coverFrameAfterRotation(asset, rotation)}`,
                    );
                }
            }
            if (flip_horizontal !== undefined) {
                if (asset.flipHorizontally === flip_horizontal) {
                    unchanged.push(`horizontal flip is already ${flip_horizontal ? 'on' : 'off'}`);
                } else {
                    asset.flipHorizontally = flip_horizontal;
                    transformChanged = true;
                    changes.push(flip_horizontal ? 'flipped horizontally' : 'horizontal flip off');
                }
            }
            if (flip_vertical !== undefined) {
                if (asset.flipVertically === flip_vertical) {
                    unchanged.push(`vertical flip is already ${flip_vertical ? 'on' : 'off'}`);
                } else {
                    asset.flipVertically = flip_vertical;
                    transformChanged = true;
                    changes.push(flip_vertical ? 'flipped vertically' : 'vertical flip off');
                }
            }
            // Scale, position, the framing presets, rotation, flips and speaker framing are crop
            // transformations — register the crop user override so a later media swap's
            // recommendation leaves the agent's framing alone.
            if (
                scale !== undefined ||
                position !== undefined ||
                framingZoom !== null ||
                transformChanged ||
                speakerCrop !== null
            ) {
                this.dispatch(
                    setCropUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            // Volume, trim, loop/hold and speed only mean something on a video. Skip them (with a
            // note) for an image rather than rejecting the whole call, so the reframe edits still land.
            if (volume !== undefined) {
                if (asset.isVideo) {
                    asset.volume = volume;
                    changes.push(`media volume to ${volume}`);
                } else {
                    notes.push(`volume was ignored because ${asset_name} contains an image`);
                }
            }

            if (wantsPlaybackEdit && !asset.isVideo) {
                notes.push(
                    `trim/loop/speed were ignored because ${asset_name} contains an image, which does not play`,
                );
            } else if (wantsPlaybackEdit) {
                if (resolvedTrim) {
                    applyUserMediaTrim(
                        scene.id,
                        asset_name,
                        resolvedTrim.start,
                        resolvedTrim.end,
                        false,
                        parentActionGroup,
                    );
                    changes.push(`trim to ${resolvedTrim.start}–${resolvedTrim.end}ms of the clip`);
                }
                if (playback_mode !== undefined) {
                    asset.playbackMode = this.toPlaybackMode(playback_mode);
                    changes.push(`playback to ${playback_mode}`);
                }
                if (playback_rate !== undefined) {
                    asset.playbackRate = playback_rate;
                    changes.push(`speed to ${playback_rate}x`);
                }
            }

            // Values that were already in place are noted alongside the edits that did land, so the
            // agent never reads "Success" as "everything I asked for moved".
            if (unchanged.length > 0 && changes.length > 0) {
                notes.push(`left as is: ${unchanged.join(', ')}`);
            }
            const noteText = notes.length > 0 ? ` NOTE: ${notes.join('; ')}.` : '';
            if (changes.length === 0 && unchanged.length > 0) {
                return `No change: ${unchanged.join(', ')} for ${asset_name} in scene ${sceneShortId}.${noteText}`;
            }
            if (changes.length === 0) {
                return `Success: Nothing was changed for asset ${asset_name} in scene ${sceneShortId}.${noteText}`;
            }

            // Share parentActionGroup so every dispatch above (overrides, the kenburn/trim operations'
            // own checkpoints) and this one collapse into a single undo step.
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            this.recordSceneFill(scene_index, 'media');

            return `Success: Updated ${changes.join(', ')} for asset ${asset_name} in scene ${sceneShortId}.${noteText}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error editing media: ${errorMessage}`;
        }
    }

    /**
     * The face to crop to, numbered left to right across the recording's picture, from face
     * detection on the frame at the scene's midpoint.
     */
    private async resolveSpeakerFace(
        sceneId: string,
        sceneShortId: string,
        speaker: number,
    ): Promise<{ face: BoundingBox; faces: BoundingBox[] } | { error: string }> {
        const { getFaceDetections } = makeOperationsCallable(
            { getFaceDetectionsOperation },
            this.luminary,
            this.dispatch,
            this._getState,
        );
        let detections: BoundingBox[];
        try {
            detections = await getFaceDetections(sceneId);
        } catch {
            return {
                error: `face detection failed for scene ${sceneShortId}. Crop it with scale and position instead.`,
            };
        }
        const faces = [...detections].sort((a, b) => a.x + a.width / 2 - (b.x + b.width / 2));
        if (faces.length === 0) {
            return {
                error: `no face was found in the recording at the middle of scene ${sceneShortId}. Crop it with scale and position instead.`,
            };
        }
        const face = faces[speaker - 1];
        if (!face) {
            const positions = faces
                .map((found, index) => `${index + 1} at the ${describeFacePlacement(found)}`)
                .join(', ');
            return {
                error: `found ${faces.length} ${faces.length === 1 ? 'face' : 'faces'} in the recording at the middle of scene ${sceneShortId} (${positions}), so there is no face ${speaker}.`,
            };
        }
        return { face, faces };
    }

    /**
     * Resolve a framing preset to the zoom the editor's "Crop to fill" / "Fit in scene" buttons
     * would apply (both centre the media), clamped to the editor's zoom range.
     */
    private resolveFraming(
        asset: TimeAwareMediaAsset,
        assetName: string,
        framing: FramingPresetArg,
    ): { zoom: number; alreadyApplied: boolean } | { error: string } {
        if (!canReframeMedia(asset)) {
            return {
                error: `${assetName} is contained inside its frame by the design and has no room to reframe`,
            };
        }
        const dimensions = getMediaCropDimensions(asset);
        if (!dimensions) {
            return {
                error: `could not determine the size of ${assetName}'s media — try again once it has loaded`,
            };
        }
        const zoom =
            framing === 'fill'
                ? Math.min(createFillCropState(dimensions).zoom, MEDIA_ZOOM.MAX_VALUE)
                : Math.max(createContainCropState(dimensions).zoom, MEDIA_ZOOM.MIN_VALUE);
        return { zoom, alreadyApplied: getMediaFramingPreset(asset) === framing };
    }

    /**
     * Rotating media that covers its frame exposes empty corners unless the zoom keeps up, so raise
     * the scale to the editor's coupled minimum (only when the media isn't deliberately zoomed out,
     * matching the inline transform controls). Returns a suffix for the change message.
     */
    private coverFrameAfterRotation(asset: TimeAwareMediaAsset, rotation: number): string {
        const { width, height } = asset.mediaTargetResolution;
        if (!canReframeMedia(asset) || asset.scaleX < 1 || !(height > 0)) {
            return '';
        }
        const minZoom = Math.min(
            calculateMinZoomFromRotation(rotation, width, height),
            MEDIA_ZOOM.MAX_VALUE,
        );
        if (asset.scaleX >= minZoom) {
            return '';
        }
        asset.scaleX = minZoom;
        asset.scaleY = minZoom;
        return ` (scale raised to ${Math.round(minZoom * 100)}% to keep the frame covered)`;
    }

    private toPlaybackMode(mode: PlaybackModeArg): string {
        return mode === 'loop' ? VideoData.PLAYBACK_LOOP : VideoData.PLAYBACK_HOLD;
    }

    /**
     * Resolve the trim window, filling whichever end the caller left out from the asset's current
     * trim (or the full source), then validating it against the source duration when known.
     */
    private resolveTrim(
        asset: TimeAwareMediaAsset,
        startTime: number | undefined,
        endTime: number | undefined,
    ): { start: number; end: number } | { error: string } {
        const sourceDuration = asset.sourceDuration;
        const start = Math.round(startTime ?? asset.startTime ?? 0);
        const resolvedEnd = endTime ?? asset.endTime ?? sourceDuration ?? undefined;
        if (resolvedEnd == null) {
            return {
                error: 'could not determine the clip end — pass end_time (milliseconds into the clip)',
            };
        }
        const end = Math.round(resolvedEnd);
        if (start < 0 || end <= start) {
            return {
                error: `invalid trim: start_time (${start}ms) must be less than end_time (${end}ms)`,
            };
        }
        if (sourceDuration != null && end > Math.round(sourceDuration)) {
            return {
                error: `end_time (${end}ms) is past the clip's ${Math.round(sourceDuration)}ms duration`,
            };
        }
        return { start, end };
    }
}
